import {  t  } from "@/client/libs/dummy-i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@stackmorph/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { getOrganizations } from "@/client/services/organization/organization";
import { useRemoveResumeShare,useShareResume } from "@/client/services/resume";

type ShareResumeDialogProps = {
  resumeId: string;
  resumeTitle: string;
  currentOrganizationId: string | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

export const ShareResumeDialog = ({ resumeId, resumeTitle, currentOrganizationId, isOpen, setIsOpen }: ShareResumeDialogProps) => {
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const { shareResume, loading: sharing } = useShareResume();
  const { removeShare, loading: removing } = useRemoveResumeShare();

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: getOrganizations,
  });

  const handleShare = async () => {
    if (!selectedOrg) {
      toast({
        variant: "error",
        title: t`Please select an organization`,
        description: t`You must select an organization to share this resume with.`,
      });
      return;
    }

    try {
      await shareResume({ id: resumeId, organizationId: selectedOrg });
      toast({
        variant: "success",
        title: t`Resume shared successfully`,
        description: t`${resumeTitle} has been shared with the organization.`,
      });
      setIsOpen(false);
    } catch (error: any) {
      toast({
        variant: "error",
        title: t`Failed to share resume`,
        description: error?.response?.data?.message ?? t`An error occurred while sharing the resume.`,
      });
    }
  };

  const handleRemove = async () => {
    try {
      await removeShare({ id: resumeId });
      toast({
        variant: "success",
        title: t`Share removed`,
        description: t`${resumeTitle} is no longer shared with any organization.`,
      });
      setIsOpen(false);
    } catch (error: any) {
      toast({
        variant: "error",
        title: t`Failed to remove share`,
        description: error?.response?.data?.message ?? t`An error occurred.`,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t`Share Resume`}</DialogTitle>
          <DialogDescription>
            {currentOrganizationId
              ? t`This resume is currently shared with an organization. You can change the organization or remove the share.`
              : t`Select an organization to share "${resumeTitle}" with. Organization members will be able to view this resume.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {currentOrganizationId ? (
            <div className="rounded-md bg-secondary p-4">
              <p className="text-sm font-medium">{t`Currently Shared`}</p>
              <p className="text-sm text-muted-foreground">
                {organizations.find((org) => org.id === currentOrganizationId)?.name ?? t`Unknown Organization`}
              </p>
            </div>
          ) : null}

          <div>
            <label htmlFor="organization" className="text-sm font-medium">
              {currentOrganizationId ? t`Change Organization` : t`Select Organization`}
            </label>
            <Select value={selectedOrg} disabled={isLoading} onValueChange={setSelectedOrg}>
              <SelectTrigger id="organization" className="mt-2">
                <SelectValue placeholder={isLoading ? t`Loading...` : t`Select an organization`} />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          {currentOrganizationId && (
            <Button variant="error" disabled={removing} onClick={handleRemove}>
              {removing ? t`Removing...` : t`Remove Share`}
            </Button>
          )}
          <Button variant="outline" onClick={() => { setIsOpen(false); }}>
            {t`Cancel`}
          </Button>
          <Button disabled={sharing || !selectedOrg} onClick={handleShare}>
            {sharing ? t`Sharing...` : (currentOrganizationId ? t`Change Organization` : t`Share Resume`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
